import { Signal, computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { SchemaSidProperty, SchemaView } from '@qtek/shared/models';
import { catchError, filter, map, mergeMap, of, pipe, tap } from 'rxjs';
import { ProductSchemaService } from './product-schema.service';

interface SchemasState {
  productSchemaViews: Record<string, SchemaView>;
  schemasSid: Record<string, SchemaSidProperty[]>;
}

const initialState: SchemasState = {
  productSchemaViews: {},
  schemasSid: {},
};

export const ProductSchemasStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(store => ({
    parcntRoleSchema: computed<Record<string, string> | undefined>(() => {
      const schema = store.productSchemaViews()['~parcnt'];
      return schema?.properties
        ?.find(prop => prop.id === '~parcnt.role')
        ?.v?.reduce((acc: Record<string, string>, { id, label }) => {
          acc[id] = label;
          return acc;
        }, {});
    }),
  })),
  withMethods((store, service = inject(ProductSchemaService)) => {
    const schemaComputedCache = new Map<
      string,
      Signal<SchemaView | undefined>
    >();
    const sidComputedCache = new Map<
      string,
      Signal<Record<string, SchemaSidProperty[]>>
    >();
    const loadingQueries = new Set<string>();
    const loadingSids = new Set<string>();

    const loadByQuery = rxMethod<string>(
      pipe(
        filter(query => {
          if (store.productSchemaViews()[query] || loadingQueries.has(query))
            return false;
          loadingQueries.add(query);
          return true;
        }),
        mergeMap(query =>
          service.getProductSchemaById(query).pipe(
            tap(({ res }) => {
              loadingQueries.delete(query);
              let schema: SchemaView;
              try {
                schema = JSON.parse(res);
              } catch (e) {
                console.error("[qtek] Error parsing schema's response: ", e);
              }
              patchState(store, state => ({
                productSchemaViews: {
                  ...state.productSchemaViews,
                  [query]: schema,
                },
              }));
            }),
            catchError(() => {
              loadingQueries.delete(query);
              return of(null);
            })
          )
        )
      )
    );

    const loadBySids = rxMethod<string[]>(
      pipe(
        map(sids =>
          sids.filter(
            sid =>
              !Array.isArray(store.schemasSid()[sid]) && !loadingSids.has(sid)
          )
        ),
        filter(missingSids => missingSids.length > 0),
        tap(missingSids => missingSids.forEach(sid => loadingSids.add(sid))),
        mergeMap(sids =>
          service.getSchemasBySids(sids).pipe(
            tap(({ res }) => {
              sids.forEach(sid => loadingSids.delete(sid));
              patchState(store, state => ({
                schemasSid: { ...state.schemasSid, ...(res ?? {}) },
              }));
            }),
            catchError(() => {
              sids.forEach(sid => loadingSids.delete(sid));
              return of(null);
            })
          )
        )
      )
    );

    return {
      loadByQuery,
      loadBySids,

      schemaByQuery(query: string): Signal<SchemaView | undefined> {
        let cached = schemaComputedCache.get(query);
        if (!cached) {
          cached = computed(() => store.productSchemaViews()[query]);
          schemaComputedCache.set(query, cached);
        }
        return cached;
      },

      sidsByKeys(sids: string[]): Signal<Record<string, SchemaSidProperty[]>> {
        const key = sids.join('\0');
        let cached = sidComputedCache.get(key);
        if (!cached) {
          cached = computed(() => {
            const storeSids = store.schemasSid();
            const result: Record<string, SchemaSidProperty[]> = {};
            for (const sid of sids) {
              if (Array.isArray(storeSids[sid])) result[sid] = storeSids[sid];
            }
            return result;
          });
          sidComputedCache.set(key, cached);
        }
        return cached;
      },
    };
  })
);
